import React from 'react';
import { ChevronLeftCircle, ChevronRightCircle } from 'lucide-react';
import { urlFor, getImageAlt } from '../../lib/sanity';
import type { SanityImage } from '../../types/sanity';

interface PetGalleryProps {
  name: string;
  mainImage: SanityImage;
  gallery?: SanityImage[];
  gender?: 'male' | 'female' | 'unknown';
  mainCategory?: 'dog' | 'cat';
}

export function PetGallery({ name, mainImage, gallery = [], gender = 'unknown', mainCategory = 'dog' }: PetGalleryProps) {
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const galleryRef = React.useRef<HTMLDivElement>(null);
  const images = [mainImage, ...(gallery || [])];
  
  const pet = { name, gender, mainCategory };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (galleryRef.current?.contains(document.activeElement)) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          navigateGallery('prev');
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          navigateGallery('next');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navigateGallery = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    } else {
      setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }
  };

  const getImageUrl = (image: SanityImage) => {
    if (!image || !image.asset) return 'https://placehold.co/800x600/jpeg';
    const imageBuilder = urlFor(image);
    if (typeof imageBuilder === 'string') return imageBuilder;
    return imageBuilder.width(800).url() || 'https://placehold.co/800x600/jpeg';
  };

  const getThumbnailUrl = (image: SanityImage) => {
    if (!image || !image.asset) return 'https://placehold.co/200x200/jpeg';
    const imageBuilder = urlFor(image);
    if (typeof imageBuilder === 'string') return imageBuilder;
    return imageBuilder.width(200).url() || 'https://placehold.co/200x200/jpeg';
  };

  return (
    <div className="space-y-4">
      <div 
        className="rounded-lg overflow-hidden bg-accessible-gray-100 dark:bg-accessible-gray-800 relative group"
        ref={galleryRef}
        tabIndex={0}
        role="region"
        aria-label="Galeria zdjęć"
        aria-roledescription="Karuzela"
        aria-live="polite"
      >
        <img
          src={getImageUrl(images[activeImageIndex])}
          alt={getImageAlt(images[activeImageIndex], pet)}
          aria-hidden={true}
          className="w-full h-auto"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            console.error('Error loading image:', e);
            (e.target as HTMLImageElement).src = 'https://placehold.co/800x600/jpeg';
          }}
        />
        <div className="sr-only" aria-live="polite">
          Zdjęcie {activeImageIndex + 1} z {images.length}
        </div>
        {images.length > 1 && (
          <>
            <button
              onClick={() => navigateGallery('prev')}
              onKeyDown={(e) => e.key === 'Enter' && navigateGallery('prev')}
              className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
              aria-label="Poprzednie zdjęcie"
            >
              <ChevronLeftCircle className="w-10 h-10 text-white drop-shadow-lg hover:text-accessible-red-600 transition-colors" />
            </button>
            <button
              onClick={() => navigateGallery('next')}
              onKeyDown={(e) => e.key === 'Enter' && navigateGallery('next')}
              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
              aria-label="Następne zdjęcie"
            >
              <ChevronRightCircle className="w-10 h-10 text-white drop-shadow-lg hover:text-accessible-red-600 transition-colors" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeImageIndex
                      ? 'bg-white scale-110'
                      : 'bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Przejdź do zdjęcia ${index + 1}`}
                  aria-current={index === activeImageIndex ? 'true' : 'false'}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-4">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveImageIndex(index)}
              onKeyDown={(e) => e.key === 'Enter' && setActiveImageIndex(index)}
              className={`rounded-lg overflow-hidden ${
                index === activeImageIndex
                  ? 'ring-2 ring-accessible-red-600'
                  : 'opacity-70 hover:opacity-100 transition-opacity'
              }`}
              aria-label={`Wybierz zdjęcie ${index + 1}`}
              aria-current={index === activeImageIndex ? 'true' : 'false'}
            >
              <img
                src={getThumbnailUrl(image)}
                alt={getImageAlt(image, pet)}
                aria-hidden="true"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  console.error('Error loading thumbnail:', e);
                  (e.target as HTMLImageElement).src = 'https://placehold.co/200x200/jpeg';
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}